<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import Column from 'primevue/column';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import KpEnergieWaermequelleSelect from '@/components/KpEnergieWaermequelleSelect.vue';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { ZuordnungWaermeerzeugerHeizung } from '@/models/ZuordnungWaermeerzeugerHeizung';
import KpTechnologieWaermeerzeugungSelect from '@/components/KpTechnologieWaermeerzeugungSelect.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import { TYPES } from '@/models/EchCode';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    ZuordnungWaermeerzeugerHeizungGwrWerte,
    ZuordnungWaermeerzeugerHeizungGwrWerteCollection,
} from '@/models/ZuordnungWaermeerzeugerHeizungGwrWerte';
import KpEchCodeSelect from '@/components/KpEchCodeSelect.vue';
import type { DataTableRowEditCancelEvent, DataTableRowEditSaveEvent } from 'primevue/datatable';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { useDeleteConfirm } from '@/composables/useDeleteConfirm';
import KpWaermeerzeugerHeizungSelect from '@/components/KpWaermeerzeugerHeizungSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const route = useRoute();
const toast = useToast();
const record: Ref<ZuordnungWaermeerzeugerHeizung | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    energie_waermequelle: createValidator(
        computed(() => record.value?.energie_waermequelle_id),
        [validateNotEmpty],
    ),
    waermeerzeuger_heizung: createValidator(
        computed(() => record.value?.waermeerzeuger_heizung_id),
        [validateNotEmpty],
    ),
    technologie_waermeerzeugung: createValidator(
        computed(() => record.value?.technologie_waermeerzeugung_id),
        [validateNotEmpty],
    ),
});

onMounted(async () => {
    loadRecord(props.id);
    if (gwrCollection.loading.value !== true) {
        gwrCollection.query();
    }
});

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'gwr_energie_waermequelle_code' },
]);
const gwrTableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-gwrTableSort`, [
    { direction: 'asc', property: 'id' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection: gwrCollection, serverTotal } = useCollection(
    new ZuordnungWaermeerzeugerHeizungGwrWerteCollection(),
    {
        autoload: false,
        tableSort: gwrTableSort,
        pageSize,
        page,
        ...{
            collectionsFilter: [
                { property: 'zuordnung_waermeerzeuger_heizung_id', value: Number(route.params.id) || -1 },
            ],
        },
    },
);

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(ZuordnungWaermeerzeugerHeizung);
    } else {
        try {
            const rec = await ZuordnungWaermeerzeugerHeizung.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            updateGwrEntries(record.value.id);
            router.push({ name: 'bg_geb_zuordnung_waermeerzeuger_heizung_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_zuordnung_waermeerzeuger_heizung' });
    }
}

function updateGwrEntries(id: number | null) {
    if (id) {
        gwrCollection.each((r) => {
            r.set({ zuordnung_waermeerzeuger_heizung_id: id });
            r.save();
        });
    }
}

const editingRows = ref<ZuordnungWaermeerzeugerHeizungGwrWerte[]>([]);
function onGwrRecordSave(event: DataTableRowEditSaveEvent) {
    event.data.waermeerzeuger_heizung_id = event.newData.waermeerzeuger_heizung_id;
    event.data.energie_waermequelle_id = event.newData.energie_waermequelle_id;
    event.data.save();
}

function newGwrRecord() {
    const mod = createModel(ZuordnungWaermeerzeugerHeizungGwrWerte);
    mod.set({ zuordnung_waermeerzeuger_heizung_id: record.value?.id });
    gwrCollection.push(mod);
    editingRows.value.push(mod);
}

const confirm = useConfirm();
const confirmDelete = useDeleteConfirm(confirm);
async function deleteRecord(r: ZuordnungWaermeerzeugerHeizungGwrWerte) {
    try {
        if (await confirmDelete()) {
            await r.destroy();
            gwrCollection.query();
        }
    } catch (e) {
        errToToast(e, toast);
    }
}

function onGwrRecordCancel(event: DataTableRowEditCancelEvent) {
    if (!event.newData.id) {
        deleteRecord(event.data as ZuordnungWaermeerzeugerHeizungGwrWerte);
    }
}
</script>

<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('zuordnung_waermeerzeuger_heizung') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerHeizungSelect
                        :label="$t('waermeerzeuger_heizung')"
                        v-model="record.waermeerzeuger_heizung_id"
                        class="col-6"
                        fluid
                        :invalid="!validators.waermeerzeuger_heizung.isValid.value"
                        :error="validators.waermeerzeuger_heizung.msg.value"
                    ></KpWaermeerzeugerHeizungSelect>
                    <KpEnergieWaermequelleSelect
                        :label="$t('energie_waermequelle')"
                        v-model="record.energie_waermequelle_id"
                        class="col-6"
                        fluid
                        :invalid="!validators.energie_waermequelle.isValid.value"
                        :error="validators.energie_waermequelle.msg.value"
                    ></KpEnergieWaermequelleSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTechnologieWaermeerzeugungSelect
                        :label="$t('technologie')"
                        v-model="record.technologie_waermeerzeugung_id"
                        class="col-6"
                        fluid
                        :invalid="!validators.technologie_waermeerzeugung.isValid.value"
                        :error="validators.technologie_waermeerzeugung.msg.value"
                    >
                    </KpTechnologieWaermeerzeugungSelect>
                    <KpNumberField :label="$t('reihenfolge')" v-model="record.reihenfolge" class="col-6" fluid>
                    </KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
                <div>
                    <KpBaseCollectionGrid
                        :disableAddBtn="!record.id"
                        :collection="gwrCollection"
                        :showRecordEditBtn="false"
                        :showCsvDownloadBtn="false"
                        editMode="row"
                        v-model:editingRows="editingRows"
                        v-model:table-sort="tableSort"
                        :title="$t('zuordnung_werte_gwr')"
                        scrollable
                        scroll-height="flex"
                        paginator
                        v-model:page="page"
                        v-model:pageSize="pageSize"
                        :total-records="serverTotal"
                        @row-edit-save="onGwrRecordSave"
                        @row-edit-cancel="onGwrRecordCancel"
                        @new="newGwrRecord"
                        @deleterecord="deleteRecord($event as ZuordnungWaermeerzeugerHeizungGwrWerte)"
                    >
                        <Column field="waermeerzeuger_heizung_id" :header="$t('waermeerzeuger_heizung')">
                            <template #body="slotProps">
                                <div v-if="slotProps.data.waermeerzeuger_heizung_i18n_key">
                                    {{ $t(slotProps.data.waermeerzeuger_heizung_i18n_key) }}
                                </div>
                                <div v-else>-</div>
                            </template>
                            <template #editor="{ data }">
                                <KpEchCodeSelect
                                    v-model="data.waermeerzeuger_heizung_id"
                                    fluid
                                    :type="TYPES.waermeerzeuger_heizung"
                                ></KpEchCodeSelect>
                            </template>
                        </Column>
                        <Column field="energie_waermequelle_id" :header="$t('energie_waermequelle')">
                            <template #body="slotProps">
                                <div v-if="slotProps.data.energie_waermequelle_i18n_key">
                                    {{ $t(slotProps.data.energie_waermequelle_i18n_key) }}
                                </div>
                                <div v-else>-</div>
                            </template>
                            <template #editor="{ data }">
                                <KpEchCodeSelect
                                    v-model="data.energie_waermequelle_id"
                                    fluid
                                    :type="TYPES.energietraeger"
                                ></KpEchCodeSelect>
                            </template>
                        </Column>
                        <Column
                            :rowEditor="true"
                            style="width: 10%; min-width: 8rem"
                            bodyStyle="text-align:center"
                        ></Column>
                    </KpBaseCollectionGrid>
                </div>
            </template>
        </Card>
        <ConfirmDialog></ConfirmDialog>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
